import React, { useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import { useLang } from "../../context/LanguageContext";

export const Icon = ({ name, className, ...rest }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} {...rest} />;
};

export const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, ...options }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, visible];
};

export const Reveal = ({ children, className, delay = 0, as: Tag = "div", ...rest }) => {
  const [ref, visible] = useReveal();
  return (
    <Tag ref={ref} className={cn("reveal", visible && "is-visible", className)} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
};

export const Container = ({ children, className }) => (
  <div className={cn("mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10", className)}>{children}</div>
);

export const Eyebrow = ({ children, className, light }) => (
  <span className={cn("inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em]", light ? "text-orange-300" : "text-brand-orange", className)}>
    <span className={cn("h-px w-8", light ? "bg-orange-300" : "bg-brand-orange")} />
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, description, align = "start", light = false, className }) => (
  <Reveal className={cn("max-w-3xl", align === "center" && "mx-auto text-center", className)}>
    {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
    <h2 className={cn("mt-4 font-heading text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem]", light ? "text-white" : "text-brand-navy")}>{title}</h2>
    {description && <p className={cn("mt-5 text-base leading-relaxed sm:text-lg", light ? "text-slate-300" : "text-slate-600")}>{description}</p>}
  </Reveal>
);

export const PageHero = ({ title, subtitle, crumbs = [], image }) => {
  const { t, isRTL } = useLang();
  const Chevron = isRTL ? Icons.ChevronLeft : Icons.ChevronRight;
  return (
    <section className="relative overflow-hidden bg-brand-navy pt-32 pb-20 text-white sm:pt-40 sm:pb-24">
      <div className="absolute inset-0 hero-grid opacity-40" />
      {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity" />}
      <div className="absolute -end-40 -top-40 h-[480px] w-[480px] rounded-full bg-brand-teal/30 blur-3xl" />
      <div className="absolute -bottom-40 start-1/4 h-[360px] w-[360px] rounded-full bg-brand-orange/20 blur-3xl" />
      <Container className="relative">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-300" aria-label="Breadcrumb">
          <Link to="/" className="transition-colors hover:text-white">{t.nav.home}</Link>
          {crumbs.map((c) => (
            <React.Fragment key={c.label}>
              <Chevron className="h-4 w-4 text-slate-500" />
              {c.to ? <Link to={c.to} className="transition-colors hover:text-white">{c.label}</Link> : <span className="text-white">{c.label}</span>}
            </React.Fragment>
          ))}
        </nav>
        <h1 className="mt-6 max-w-4xl font-heading text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-6xl animate-fade-up">{title}</h1>
        {subtitle && <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300 animate-fade-up" style={{ animationDelay: "120ms" }}>{subtitle}</p>}
      </Container>
    </section>
  );
};

export const ArrowFwd = ({ className }) => {
  const { isRTL } = useLang();
  const A = isRTL ? Icons.ArrowLeft : Icons.ArrowRight;
  return <A className={className} />;
};

export const CTASection = ({ onQuote }) => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-brand-teal py-20 text-white">
      <div className="absolute inset-0 hero-grid opacity-20" />
      <div className="absolute -start-20 top-0 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -end-10 bottom-0 h-72 w-72 rounded-full bg-brand-orange/40 blur-3xl" />
      <Container className="relative">
        <Reveal className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div className="max-w-2xl">
            <Eyebrow className="text-orange-200">{t.sections.ctaEyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">{t.sections.ctaTitle}</h2>
            <p className="mt-4 text-lg text-teal-50/90">{t.sections.ctaDesc}</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" data-testid="cta-contact-link" className="btn-primary bg-white text-brand-navy hover:bg-slate-100">
              {t.sections.contactUs} <ArrowFwd className="h-4 w-4" />
            </Link>
            <button type="button" onClick={onQuote} data-testid="cta-quote-button" className="btn-primary bg-brand-orange text-white hover:bg-orange-600">{t.nav.quote}</button>
          </div>
        </Reveal>
      </Container>
    </section>
  );
};

export const Counter = ({ value, suffix = "", duration = 1600 }) => {
  const [ref, visible] = useReveal();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!visible) return undefined;
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, value, duration]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export const WhatsAppButton = () => {
  const { t, c } = useLang();
  const number = c.COMPANY.phoneHref.replace(/[^0-9]/g, "");
  const href = `https://wa.me/${number}?text=${encodeURIComponent(t.whatsapp.message)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={t.whatsapp.label}
      data-testid="whatsapp-button"
      className="group fixed bottom-6 start-6 z-40 flex items-center gap-3 rounded-full bg-[#25D366] py-3 ps-3 pe-3 text-white shadow-lg shadow-[#25D366]/40 transition-colors hover:bg-[#1ebe5b] sm:pe-5"
    >
      <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#25D366]/40" style={{ animationDuration: "2.5s" }} />
      <svg viewBox="0 0 32 32" className="h-7 w-7 fill-current" aria-hidden="true">
        <path d="M16.004 3C8.83 3 3 8.83 3 16.004c0 2.29.6 4.53 1.74 6.5L3 29l6.68-1.72a12.94 12.94 0 0 0 6.32 1.62c7.17 0 13-5.83 13-13S23.17 3 16.004 3zm0 23.7c-1.98 0-3.92-.53-5.6-1.54l-.4-.24-3.96 1.02 1.06-3.86-.26-.4a10.66 10.66 0 0 1-1.64-5.68c0-5.9 4.8-10.7 10.8-10.7 5.9 0 10.7 4.8 10.7 10.7s-4.8 10.7-10.7 10.7zm5.87-8.02c-.32-.16-1.9-.94-2.2-1.04-.3-.1-.5-.16-.72.16-.22.32-.82 1.04-1 1.26-.18.2-.38.24-.7.08-.32-.16-1.36-.5-2.6-1.6a9.7 9.7 0 0 1-1.8-2.24c-.18-.32-.02-.5.14-.66.14-.14.32-.38.48-.56.16-.2.22-.32.32-.54.1-.22.06-.4-.02-.56-.08-.16-.72-1.74-.98-2.38-.26-.62-.52-.54-.72-.54h-.6c-.2 0-.56.08-.84.4-.3.32-1.1 1.08-1.1 2.62s1.12 3.04 1.28 3.26c.16.2 2.22 3.38 5.36 4.74.75.32 1.34.52 1.8.66.75.24 1.44.2 1.98.12.6-.08 1.9-.78 2.16-1.52.26-.76.26-1.4.18-1.52-.08-.14-.28-.22-.6-.38z" />
      </svg>
      <span className="hidden text-sm font-semibold sm:block">{t.whatsapp.label}</span>
    </a>
  );
};
